import { LitElement, html, css, nothing } from "lit";
import type { PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import { resolveImageFallbackSrc } from "./srcset-utils.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyImageLoading = "lazy" | "eager";
export type SkyImageDecoding = "async" | "sync" | "auto";

/**
 * @element sky-image
 *
 * @summary Responsive image component with style controls, `srcset`, and optional auto `sizes`.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/image
 *
 * @slot - No slots are exposed; image content is rendered internally.
 * @property {string} src - Primary image source URL.
 * @property {string} alt - Alternative text for accessibility.
 * @property {string} width - CSS width for the rendered image.
 * @property {string} height - CSS height for the rendered image.
 * @property {string | null} aspectRatio - CSS `aspect-ratio` value.
 * @property {string} borderRadius - CSS border radius.
 * @property {string} boxShadow - CSS box shadow.
 * @property {string} objectFit - CSS object-fit value.
 * @property {string} objectPosition - CSS object-position value.
 * @property {string} srcset - Responsive source set string.
 * @property {string} sizes - Responsive sizes string.
 * @property {boolean} autoSizes - Computes `sizes` from host width when true.
 * @property {SkyImageLoading} loading - Native loading hint.
 * @property {SkyImageDecoding} decoding - Native decoding hint.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @csspart img - The underlying `<img>` element.
 *
 * @Behavior
 * - Renders a fallback visual placeholder when `src` is empty
 * - Supports automatic `sizes` updates using ResizeObserver
 * - Forwards native image loading and decoding hints
 *
 * @example
 * ```html
 * <sky-image
 *   src="/images/photo-1280.jpg"
 *   srcset="/images/photo-640.jpg 640w, /images/photo-960.jpg 960w, /images/photo-1280.jpg 1280w"
 *   sizes="(max-width: 768px) 100vw, 50vw"
 *   alt="Responsive photo"
 *   width="100%"
 *   aspect-ratio="4/3"
 *   border-radius="8px"
 *   loading="lazy"
 * ></sky-image>
 * ```
 * ```vue
 * <template>
 *   <sky-image
 *     src="/images/photo-1280.jpg"
 *     srcset="/images/photo-640.jpg 640w, /images/photo-960.jpg 960w, /images/photo-1280.jpg 1280w"
 *     sizes="(max-width: 768px) 100vw, 50vw"
 *     alt="Responsive photo"
 *     width="100%"
 *     aspect-ratio="4/3"
 *     border-radius="8px"
 *   ></sky-image>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-image
 *       src="/images/photo-1280.jpg"
 *       srcset="/images/photo-640.jpg 640w, /images/photo-960.jpg 960w, /images/photo-1280.jpg 1280w"
 *       sizes="(max-width: 768px) 100vw, 50vw"
 *       alt="Responsive photo"
 *       width="100%"
 *       aspect-ratio="4/3"
 *       border-radius="8px"
 *     ></sky-image>
 *   );
 * }
 * ```
 */
@customElement("sky-image")
export class SkyImage extends LitElement {
  @property({ type: String }) src = "";
  @property({ type: String }) alt = "Image";
  @property({ type: String }) width = "auto";
  @property({ type: String }) height = "auto";
  @property({ type: String }) aspectRatio: string | null = null;
  @property({ type: String }) borderRadius = "0";
  @property({ type: String }) boxShadow = "none";
  @property({ type: String }) objectFit = "cover";
  @property({ type: String }) objectPosition = "center";

  @property({ type: String }) srcset = "";
  @property({ type: String }) sizes = "";

  @property({ type: Boolean }) autoSizes = false;
  private _computedSizes = "";

  @property({ type: String }) loading: SkyImageLoading = "lazy";
  @property({ type: String }) decoding: SkyImageDecoding = "async";


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: inline-block;
      max-width: 100%;
    }

    img,
    .placeholder {
      display: block;
      max-width: 100%;
    }

    .placeholder {
      background: var(--sky-hover-tertiary);
    }
  `;

  private _ro?: ResizeObserver;
  private _sizesRafId: number | null = null;
  private _updateSizes = () => {
    if (this._sizesRafId != null) return;
    this._sizesRafId = requestAnimationFrame(() => {
      this._sizesRafId = null;
      const w = Math.ceil(this.getBoundingClientRect().width);
      if (w > 0) {
        const next = `${w}px`;
        if (this._computedSizes === next) return;
        this._computedSizes = next;
        const img = this.renderRoot.querySelector("img");
        if (img instanceof HTMLImageElement) img.sizes = next;
      }
    });
  };

  override connectedCallback(): void {
    super.connectedCallback();
    // firstUpdated runs only once; re-create the observer when the element
    // is moved back into the document after a disconnect.
    this._ensureAutoSizesObserver();
  }

  override firstUpdated(): void {
    this._ensureAutoSizesObserver();
  }

  private _ensureAutoSizesObserver(): void {
    if (!this.autoSizes) return;
    if (!this._ro) {
      this._ro = new ResizeObserver(this._updateSizes);
      this._ro.observe(this);
    }
    this._updateSizes();
  }

  override updated(changed: PropertyValues<this>): void {
    super.updated(changed);

    if (changed.has("autoSizes")) {
      if (this.autoSizes) {
        this._ensureAutoSizesObserver();
      } else {
        this._ro?.disconnect();
        this._ro = undefined;
        this._computedSizes = "";
        const img = this.renderRoot.querySelector("img");
        if (img instanceof HTMLImageElement) {
          if (this.sizes) img.sizes = this.sizes;
          else img.removeAttribute("sizes");
        }
      }
    }

    if (!this._shouldResyncImageSource(changed)) return;
    this._resyncImageSource(
      changed.has("srcset") && changed.get("srcset") !== undefined,
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._ro?.disconnect();
    this._ro = undefined;
    if (this._sizesRafId != null) {
      cancelAnimationFrame(this._sizesRafId);
      this._sizesRafId = null;
    }
  }

  /** Browsers keep the resolved resource when only srcset/sizes attrs change on a live <img>. */
  private _shouldResyncImageSource(changed: PropertyValues<this>): boolean {
    if (!this.src?.trim() && !this.srcset?.trim()) return false;
    if (changed.has("src") && changed.get("src") !== undefined) return true;
    if (changed.has("srcset") && changed.get("srcset") !== undefined) return true;
    if (changed.has("sizes") && changed.get("sizes") !== undefined) return true;
    return false;
  }

  private _resolvedSizesAttr(): string {
    return this.autoSizes ? this._computedSizes : this.sizes || "";
  }

  /** `src` used on <img> — must stay in sync with `srcset` when the set changes. */
  private _displaySrc(): string {
    const explicit = this.src?.trim() ?? "";
    const srcset = this.srcset?.trim() ?? "";
    if (!explicit && !srcset) return "";
    if (!srcset) return explicit;
    return resolveImageFallbackSrc(explicit, srcset);
  }

  private _applyImgAttrs(img: HTMLImageElement, srcsetChanged: boolean): void {
    const srcset = this.srcset?.trim() ?? "";
    const sizesAttr = this._resolvedSizesAttr();
    const src = this._displaySrc();

    if (srcset) img.srcset = srcset;
    else img.removeAttribute("srcset");

    if (sizesAttr) img.sizes = sizesAttr;
    else img.removeAttribute("sizes");

    // When the candidate list changes, drop `src` first so the UA cannot keep the old resource.
    if (srcsetChanged) img.removeAttribute("src");
    img.src = src;
  }

  private _resyncImageSource(srcsetChanged = false): void {
    const img = this.renderRoot.querySelector("img");
    if (!(img instanceof HTMLImageElement)) return;

    const replacement = img.cloneNode(false) as HTMLImageElement;
    replacement.alt = this.alt;
    replacement.loading = this.loading;
    replacement.decoding = this.decoding;
    const style = img.getAttribute("style");
    if (style) replacement.setAttribute("style", style);
    replacement.setAttribute("part", "img");

    this._applyImgAttrs(replacement, srcsetChanged);
    img.replaceWith(replacement);
  }

  override render() {
    const sizesAttr = this._resolvedSizesAttr();
    const displaySrc = this._displaySrc();
    const hasSrc = displaySrc !== "";

    const styleVars = {
      width: this.width,
      height: this.height,
      borderRadius: this.borderRadius,
      boxShadow: this.boxShadow,
      objectFit: this.objectFit,
      objectPosition: this.objectPosition,
      aspectRatio: this.aspectRatio ?? "auto",
    };

    if (!hasSrc) {
      return html`
        <div
          class="placeholder"
          part="img"
          style=${styleMap(styleVars)}
          aria-label=${this.alt}
          role="img"
        ></div>
      `;
    }

    return html`
      <img
        src=${displaySrc}
        alt=${this.alt}
        srcset=${this.srcset || nothing}
        sizes=${sizesAttr || nothing}
        loading=${this.loading}
        decoding=${this.decoding}
        style=${styleMap(styleVars)}
        part="img"
      />
    `;
  }
}
